import { useQuery } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import type { Pricing } from "@mysnowscape/snow-lib";
import { confirmWrapper } from "../Modals/ConfirmWrapper";

export const PARK_MODE_PLAN_ID = "park-mode-2026";
const PARK_MODE_PRICING_UNAVAILABLE =
  "Park mode pricing is unavailable. Please refresh and try again.";

export function isParkPlan(planId?: string): boolean {
  return planId?.startsWith("park-mode") ?? false;
}

function getParkPlan(
  plans: Pricing.PlanDetails[] = [],
  planId = PARK_MODE_PLAN_ID,
): Pricing.PlanDetails | undefined {
  return plans.find(
    (plan) =>
      plan.id === planId &&
      plan.active &&
      Number.isFinite(plan.price) &&
      plan.price >= 0,
  );
}

function getParkModeDescription(plan: Pricing.PlanDetails): string {
  const price = Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(plan.price);
  return `Park mode keeps your data saved between seasons for ${price}/month. Active plan features pause while parked. You can switch back to an active plan anytime in Billing / Subscriptions.`;
}

export function useParkModePlan() {
  const query = useQuery<{ plans: Pricing.PlanDetails[] }>({
    queryKey: ["stripe/pricing"],
  });
  const plan = query.isError ? undefined : getParkPlan(query.data?.plans);
  return {
    plan,
    description: plan
      ? getParkModeDescription(plan)
      : query.isLoading
        ? "Loading Park mode pricing..."
        : PARK_MODE_PRICING_UNAVAILABLE,
  };
}

export async function confirmParkMode(
  queryClient: QueryClient,
  planId = PARK_MODE_PLAN_ID,
) {
  const pricingData = await queryClient.ensureQueryData<{
    plans: Pricing.PlanDetails[];
  }>({
    queryKey: ["stripe/pricing"],
  });
  const parkPlan = getParkPlan(pricingData?.plans, planId);
  if (!parkPlan) throw new Error(PARK_MODE_PRICING_UNAVAILABLE);
  return confirmWrapper("Switch to Park mode?", {
    description: getParkModeDescription(parkPlan),
    okText: "Switch to Park mode",
    cancelText: "Keep current plan",
  });
}
